import { Helmet } from 'react-helmet-async';
import s from './WelcomeBackIdentificationStatus.module.scss';
import classes from 'classnames';
import HelpInfoDefault from 'components/HelpInfo/HelpInfoDefault';
import IdentificationStatus from './IdentificationStatus';
import Button from 'components/Button/Button';
import useWelcomeBackIdentificationStatus from './useWelcomeBackIdentificationStatus';
import useWelcomeBackOverallStatus from './useWelcomeBackOverallStatus';
import IdentificationStatusEmpty from './IdentificationStatus/IdentificationStatusEmpty';
import { Card, Typography, RefreshButton } from 'components';
import { useTranslation } from 'react-i18next';
import { getIsProdEnv } from 'utils/getEnv';

export default function WelcomeBackIdentificationStatus() {
  const { t } = useTranslation(['welcome_back', 'common', 'bank_id_error']);
  const {
    identityStatusProps,
    bankAccountStatusProps,
    bothStatusesAreApproved,
    handleBothApprovedClick,
    fetchStatus,
    isForbiddenIdentificationStatus,
  } = useWelcomeBackIdentificationStatus();

  const { overallStatusText } = useWelcomeBackOverallStatus();

  const renderStatus = (props: typeof identityStatusProps) =>
    props ? <IdentificationStatus {...props} /> : <IdentificationStatusEmpty />;

  return (
    <>
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>

      {isForbiddenIdentificationStatus ? (
        <>
          <Typography
            kind="h1"
            size="xl"
            color="primary"
            className={s.textCenter}
          >
            {t('bank_id_error:foreigner.title')}
          </Typography>
          <Typography size="s" className={classes(s.description, s.textCenter)}>
            {t('bank_id_error:foreigner.description')}
          </Typography>

          <div className={s.helpInfo}>
            <HelpInfoDefault />
          </div>
        </>
      ) : (
        <>
          <Typography
            className={s['card-title']}
            size="l"
            fontWeight={600}
            dataTest="IdentificationStatusEmpty-title"
          >
            {t('title')}
          </Typography>

          <Card className={s.card} dataTest="IdentificationStatusEmpty-card">
            {renderStatus(identityStatusProps)}

            <div className={s.divider} />

            {renderStatus(bankAccountStatusProps)}

            <div className={s.divider} />

            {overallStatusText && (
              <>
                <div className="flex items-center">
                  <span className="text-sm">{overallStatusText}</span>
                </div>

                <div className="flex justify-center my-4">
                  <RefreshButton
                    // 5 minutes for production, 10 seconds for other environments
                    delaySeconds={getIsProdEnv() ? 300 : 10}
                    onRefresh={fetchStatus}
                    dataTest="welcome_back.refresh.button"
                    text={t('refresh_button_text')}
                  />
                </div>
              </>
            )}

            {bothStatusesAreApproved && (
              <Button
                onClick={handleBothApprovedClick}
                className={s.button}
                dataTest="welcome_back.both_approved.button"
              >
                {t('common:continue')}
              </Button>
            )}

            <div className="mt-10">
              <HelpInfoDefault />
            </div>
          </Card>
        </>
      )}
    </>
  );
}
